/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import { $createImageNode } from "abreact/input/markdown/nodes/ImageNode";
import { StringUtils } from "abweb/utils/StringUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import { $createLinkNode } from "@lexical/link";
import { $createTextNode, $isParagraphNode, $isTextNode } from "lexical";
import { $createAbLinkNode } from "abreact/input/markdown/nodes/AbLinkNode";
let noPersonMatches;
export const getNoPersonMatches = () => {
  if (noPersonMatches === undefined) {
    noPersonMatches = {
      id: null,
      // pick a non-string value so extensions don't override
      displayName: AbReactResources.markdownEditor_noPersonMatches()
    };
  }
  return noPersonMatches;
};
let personExtension;
export const getPersonExtension = () => {
  if (personExtension === undefined) {
    try {
      personExtension = AbMarkdownExtensions.markdownPersonExtension.get();
    } catch (e) {
      personExtension = null;
    }
  }
  return personExtension;
};
let inputFieldExtension;
export const getInputFieldExtension = () => {
  if (inputFieldExtension === undefined) {
    try {
      inputFieldExtension = AbMarkdownExtensions.markdownInputFieldExtension.get();
    } catch (e) {
      inputFieldExtension = null;
    }
  }
  return inputFieldExtension;
};
let paramFieldExtension;
export const getParamFieldExtension = () => {
  if (paramFieldExtension === undefined) {
    try {
      paramFieldExtension = AbMarkdownExtensions.markdownParamFieldExtension.get();
    } catch (e) {
      paramFieldExtension = null;
    }
  }
  return paramFieldExtension;
};
let abstractFieldExtension;
export const getAbstractFieldExtension = () => {
  if (abstractFieldExtension === undefined) {
    try {
      abstractFieldExtension = AbMarkdownExtensions.markdownAbstractFieldExtension.get();
    } catch (e) {
      abstractFieldExtension = null;
    }
  }
  return abstractFieldExtension;
};
let noAbstractFieldMatches;
export const getNoAbstractFieldMatches = () => {
  if (noAbstractFieldMatches === undefined) {
    noAbstractFieldMatches = {
      displayName: "none",
      fieldDef: {
        techName: null,
        typeIcon: "null",
        // pick a non-string value so extensions don't override
        canonicalName: "none",
        businessName: "none"
      }
    };
  }
  return noAbstractFieldMatches;
};
export let FIELD_TYPE = /*#__PURE__*/function (FIELD_TYPE) {
  FIELD_TYPE["INPUT"] = "input";
  FIELD_TYPE["PARAM"] = "param";
  FIELD_TYPE["NONE"] = "none";
  return FIELD_TYPE;
}({});
export function buildParameterMarkdown(techName, displayName, typeIcon) {
  let display = StringUtils.isNullOrEmpty(displayName) ? techName : displayName;
  return `[${display}](param:${techName}:${typeIcon ?? "string"})`;
}
export function buildInputMarkdown(displayName, canonicalName, businessName, breEncoding, typeIcon) {
  return `[${displayName}](input:${canonicalName}:${businessName}:${breEncoding}:${typeIcon})`;
}
export function convertMarkdownToBreRuleString(input) {
  // Regular expression to match tokens in the format [token](...)
  // Captures both the token and the entire token structure
  const regex = /(\[[^\]]+\]\([input|param].*?\))/g;

  // Split the string on tokens and save the parts
  const parts = input.split(regex);

  // Reconstruct the desired output format
  let output = "";
  if (parts.length === 1) {
    //nothing to concat
    output = `'${escapeSpecialChars(parts[0])}'`;
  } else {
    output = "string_concat(";
    for (let i = 0; i < parts.length; i++) {
      if (regex.test(parts[i])) {
        // If the part matches the regex, it is a token
        let split = parts[i].split(":");
        let val = split[1];
        if (split[0].includes("param")) {
          val = `%[P|${val}]`;
          output += val;
        } else {
          const type = split.pop()?.slice(0, -1);
          const cast = type !== "string" && type !== "date-time" ? `(string(''))(decimal('')) ` : ``;
          let x = split[3].slice(0, -1);
          val = `%[I|${x}|Input 1]`;
          output += `(is_null(${val}) ? 'NULL' : ${cast}${val})`;
        }
      } else {
        if (StringUtils.isNullOrEmpty(parts[i])) {
          continue;
        }
        // Otherwise, treat it as a string fragment
        output += `'${escapeSpecialChars(parts[i])}'`; // Wrap in single quotes and trim any excess whitespace
      }
      if (i < parts.length - 1) {
        output += ', '; // Add concatenation operator between parts
      }
    }
    if (output.endsWith(", ")) {
      output = output.slice(0, -2);
    }
    output += ")";
  }
  return output;
}
export function escapeSpecialChars(val) {
  // Use a regular expression to replace characters with their escaped versions
  return val.replace(/["'\n]/g, char => {
    switch (char) {
      case '"':
        return '\\"';
      case "'":
        return "\\'";
      case "\n":
        return "\\n";
      default:
        return char;
    }
  });
}
export function createImageNode(altText, url, width, height) {
  // if no width or height passed in, try to get them from the URL
  if (width === undefined || height === undefined) {
    if (url.startsWith("data:")) {
      const matchWidth = url.match(/width=(\d+(?:\.?\d+)?);/);
      if (matchWidth && width === undefined) {
        width = parseFloat(matchWidth[1]);
        if (isNaN(width)) width = undefined;
      }
      const matchHeight = url.match(/height=(\d+(?:\.?\d+)?);/);
      if (matchHeight && height === undefined) {
        height = parseFloat(matchHeight[1]);
        if (isNaN(height)) height = undefined;
      }
    } else {
      const questionMarkPos = url.indexOf("?");
      let queryStringParameters = {};
      if (questionMarkPos >= 0) {
        const queryString = url.substring(questionMarkPos + 1);
        queryStringParameters = URLUtils.queryStringToObject(queryString);
      }
      if (width === undefined) width = parseFloat(queryStringParameters["width"]);
      if (isNaN(width)) width = undefined;
      if (height === undefined) height = parseFloat(queryStringParameters["height"]);
      if (isNaN(height)) height = undefined;
    }
  }
  const resizable = true; //!url.startsWith("data:"); // embedded images are not resizable

  const imageNode = $createImageNode({
    altText,
    //    maxWidth: 800,
    width,
    height,
    src: url,
    captionsEnabled: false,
    // markdown does not support captions
    resizable
  });
  return imageNode;
}
export function createLinkNode(linkText, linkUrl, linkTitle, textNode) {
  let protocol = URLUtils.getProtocol(linkUrl) || "http";
  const linkNode = $createLinkNode(linkUrl, {
    title: linkTitle
  });
  if (standard_protocols.has(protocol)) {
    const linkTextNode = $createTextNode(linkText);
    if (textNode) linkTextNode.setFormat(textNode.getFormat());
    linkNode.append(linkTextNode);
  } else {
    // non-standard protocol, make ablink node
    const abLinkNode = $createAbLinkNode(linkText, linkUrl);
    linkNode.append(abLinkNode);
  }
  return linkNode;
}
export const standard_protocols = new Set(['http', 'https', 'mailto', 'sms', 'tel']);
const MARKDOWN_EMPTY_LINE_REG_EXP = /^\s{0,3}$/;
export function isEmptyParagraph(node) {
  if (!$isParagraphNode(node)) {
    return false;
  }
  const firstChild = node.getFirstChild();
  return firstChild == null || node.getChildrenSize() === 1 && $isTextNode(firstChild) && MARKDOWN_EMPTY_LINE_REG_EXP.test(firstChild.getTextContent());
}
export function indexBy(list, callback) {
  const index = {};
  for (const item of list) {
    const key = callback(item);
    if (index[key]) {
      index[key].push(item);
    } else {
      index[key] = [item];
    }
  }
  return index;
}
export function transformersByType(transformers) {
  const byType = indexBy(transformers, t => t.type);
  return {
    element: byType.element || [],
    textFormat: byType['text-format'] || [],
    textMatch: byType['text-match'] || []
  };
}
export const PUNCTUATION_OR_SPACE = /[!-/:-@[-`{-~\s]/;
export function startsWithRichContent(text) {
  // Check for blockquotes
  if (text.startsWith("> ")) {
    return true;
  }

  // Check for unordered lists
  if (text.startsWith("* ") || text.startsWith("- ") || text.startsWith("+ ")) {
    return true;
  }

  // Check for ordered lists (any number followed by '. ')
  if (/^\d+\. /.test(text)) {
    return true;
  }

  // Check for headers (starts with 1-6 of '#', followed by space)
  if (/^#{1,6} /.test(text)) {
    return true;
  }

  // Check for links/images
  if (text.startsWith("[") || text.startsWith("![")) {
    return true;
  }

  // Check for tables
  if (text.startsWith("|")) {
    return true;
  }

  // Check for code blocks
  if (text.startsWith("```")) {
    return true;
  }

  // Check for horizontal rules
  const hrStrs = ["***", "___", "---"];
  for (const hrStr of hrStrs) {
    // If it's only an hr, it starts with rich text
    if (hrStr === text) {
      return true;
    }

    // Otherwise it only starts with an hr if the hr is on a line by itself
    if (text.startsWith(`${hrStr}\n`)) {
      return true;
    }

    // This means starting the markdown with e.g. "---a"
    // does NOT start with an hr.
  }
  return false;
}
//# sourceMappingURL=MarkdownUtils.js.map
